import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";

const empresaId = localStorage.getItem("empresaId") || "1";

export default function RecepcionMercancia() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [ordenesPendientes, setOrdenesPendientes] = useState([]);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [recepciones, setRecepciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [cantidadesRecibidas, setCantidadesRecibidas] = useState({});
  const [fechaRecepcion, setFechaRecepcion] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendientesRes, recepcionesRes] = await Promise.all([
        axios.get(API_ENDPOINTS.COMPRAS_PENDIENTES, { params: { empresa_id: empresaId } }),
        axios.get(API_ENDPOINTS.RECEPCIONES, { params: { empresa_id: empresaId } }),
      ]);
      setOrdenesPendientes(pendientesRes.data);
      setRecepciones(recepcionesRes.data);
    } catch (error) {
      console.error("Error al cargar datos de recepción:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrden = async (e, newValue) => {
    setOrdenSeleccionada(newValue);
    if (newValue) {
      // Inicializar cantidades recibidas con 0 o la cantidad restante
      const iniciales = {};
      newValue.detalles.forEach((det) => {
        iniciales[det.id] = det.cantidad - (det.cantidad_recibida || 0);
      });
      setCantidadesRecibidas(iniciales);
    } else {
      setCantidadesRecibidas({});
    }
  };

  const handleCantidadChange = (detalleId, value) => {
    setCantidadesRecibidas((prev) => ({
      ...prev,
      [detalleId]: parseFloat(value) || 0,
    }));
  };

  const registrarRecepcion = async () => {
    if (!ordenSeleccionada) {
      alert("Seleccione una orden de compra");
      return;
    }

    const detalles = Object.entries(cantidadesRecibidas)
      .filter(([_, cant]) => cant > 0)
      .map(([id, cant]) => ({
        detalle_compra_id: parseInt(id),
        cantidad_recibida: cant,
      }));

    if (detalles.length === 0) {
      alert("Ingrese al menos una cantidad para recibir.");
      return;
    }

    setLoadingRegistro(true);
    try {
      const dataToSend = {
        compra_id: ordenSeleccionada.id,
        empresa_id: empresaId,
        fecha_recepcion: fechaRecepcion,
        comentarios: "Registro desde el frontend",
        detalles: detalles,
      };

      await axios.post(API_ENDPOINTS.RECEPCIONES, dataToSend);
      alert("Recepción registrada exitosamente");
      setOrdenSeleccionada(null);
      setCantidadesRecibidas({});
      fetchData(); // Recargar datos
    } catch (error) {
      console.error("Error al registrar recepción:", error);
      alert("Error al registrar recepción. Verifique cantidades e intente de nuevo.");
    } finally {
      setLoadingRegistro(false);
    }
  };

  const columns = [
    { Header: "Folio", accessor: "folio", width: "15%" },
    { Header: "Orden Compra", accessor: "ordenCompra", width: "15%" },
    { Header: "Proveedor", accessor: "proveedor", width: "30%" },
    { Header: "Fecha", accessor: "fecha", width: "15%" },
    { Header: "Comentarios", accessor: "comentarios", width: "25%" },
  ];

  const rows = recepciones.map((rec) => ({
    folio: rec.folio || `REC-${rec.id}`,
    ordenCompra: rec.compra?.folio || "N/A",
    proveedor: rec.compra?.proveedor?.nombre || "N/A",
    fecha: new Date(rec.fecha_recepcion).toLocaleDateString("es-MX"),
    comentarios: rec.comentarios || "",
  }));

  return (
    <MDBox>
      <Grid container spacing={3}>
        {/* Formulario de Recepción */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>local_shipping</Icon>
                Registrar Recepción de Mercancía
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={ordenesPendientes}
                    getOptionLabel={(option) =>
                      `${option.folio} - ${option.proveedor?.nombre} (${option.detalles?.length} items)`
                    }
                    value={ordenSeleccionada}
                    onChange={handleSelectOrden}
                    loading={loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Orden de Compra Pendiente *"
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    type="date"
                    label="Fecha de Recepción"
                    fullWidth
                    value={fechaRecepcion}
                    onChange={(e) => setFechaRecepcion(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDButton
                    variant="gradient"
                    color={sidenavColor}
                    fullWidth
                    onClick={registrarRecepcion}
                    disabled={loadingRegistro || !ordenSeleccionada}
                  >
                    <Icon sx={{ mr: 1 }}>save</Icon>
                    {loadingRegistro ? "Guardando..." : "Registrar"}
                  </MDButton>
                </Grid>

                {ordenSeleccionada && (
                  <Grid item xs={12} mt={2}>
                    <Card variant="outlined">
                      <MDBox p={2}>
                        <MDTypography variant="subtitle2" mb={1}>
                          Productos por recibir / Cantidades restates:
                        </MDTypography>
                        <Grid container spacing={2} mb={1} sx={{ borderBottom: "1px solid #eee", pb: 1 }}>
                          <Grid item xs={6}><MDTypography variant="caption" fontWeight="bold">Producto</MDTypography></Grid>
                          <Grid item xs={2} textAlign="center"><MDTypography variant="caption" fontWeight="bold">Pendiente</MDTypography></Grid>
                          <Grid item xs={4} textAlign="center"><MDTypography variant="caption" fontWeight="bold">Recibir Ahora</MDTypography></Grid>
                        </Grid>
                        {ordenSeleccionada.detalles.map((det) => (
                          <Grid container spacing={2} key={det.id} alignItems="center" mb={1}>
                            <Grid item xs={6}>
                              <MDTypography variant="body2">{det.producto?.nombre}</MDTypography>
                            </Grid>
                            <Grid item xs={2} textAlign="center">
                              <MDTypography variant="body2">
                                {det.cantidad - (det.cantidad_recibida || 0)}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={cantidadesRecibidas[det.id] || 0}
                                onChange={(e) => handleCantidadChange(det.id, e.target.value)}
                                inputProps={{ min: 0, max: det.cantidad - (det.cantidad_recibida || 0) }}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </MDBox>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </MDBox>
          </Card>
        </Grid>

        {/* Historial de Recepciones */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>history</Icon>
                Historial de Recepciones
              </MDTypography>
              {loading ? (
                <MDTypography variant="body2" color="text" textAlign="center" py={3}>Cargando historial...</MDTypography>
              ) : recepciones.length === 0 ? (
                <MDTypography variant="body2" color="text" textAlign="center" py={3}>
                  No hay recepciones registradas
                </MDTypography>
              ) : (
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  noEndBorder
                />
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
